import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { RestfulApiService } from '../restful-api.service';

/**
 * Currency interface
 */
export interface Currency {
  uuid: string;
  name: string;
  exchangeRate: string;
}

/**
 * Language interface for general dropdowns
 */
export interface GeneralLanguage {
  uuid: string;
  code: string;
  name: string;
  flag: string;
  state: 'LIVE' | 'DRAFT';
  lastModified: number;
  primaryLanguage: boolean;
}

/**
 * Country entry from the general dropdowns (bound and sent back as-is, e.g. by the contact-us form)
 */
export interface GeneralCountry {
  uuid: string;
  name: string;
}

/**
 * General dropdowns response interface
 */
export interface GeneralDropdownsResponse {
  response: {
    currencies: Currency[];
    languages: GeneralLanguage[];
    countries?: GeneralCountry[];
  };
}

/**
 * File upload response interface
 */
export interface FileUploadResponse {
  response: {
    uuid: string;
    url: string;
    fileName: string;
    fileSize: number;
    mimeType: string;
    objectUuid?: string;
  };
}

/**
 * Champion file types enum
 */
export enum ChampionFileType {
  IMAGE = 'CHAMPION_HIGH_QUALITY_IMAGE',
  VIDEO = 'CHAMPION_ENTITY_VIDEO',
  DOCUMENT = 'CHAMPION_ENTITY_DOCUMENT'
}

/**
 * File upload request parameters
 */
export interface FileUploadParams {
  entityUuid: string;
  fileType: string;
  secondUuid?: string;
  file: File;
}

/**
 * File delete request parameters
 */
export interface FileDeleteParams {
  entityUuid: string;
  fileType: string;
  fileUuid: string;
}

/**
 * Single key-value entry from the globalConfig array
 */
export interface GeneralSettingsConfigItem {
  key: string;
  value: string;
}

/**
 * A single row in the homepage carousel configuration
 */
export interface CarouselRow {
  position: number;
  entityType: string;
  filterType: string;
}

/**
 * Homepage focus / carousel configuration
 */
export interface HomepageFocus {
  enableMainCarousel: boolean;
  timer: number;
  type: string;
  filter: string;
  carouselRows: CarouselRow[];
}

/**
 * Response shape for GET /v2/general/settings
 */
export interface GeneralSettingsResponse {
  response: {
    globalConfig: GeneralSettingsConfigItem[];
    homepageFocus: HomepageFocus;
  };
}

/**
 * Payload for POST /v1/general/settings/create-update
 * The value can be a string, number, or object (serialised to JSON string by the caller)
 */
export interface CreateUpdateGeneralSettingsPayload {
  key: string;
  value: string | number | object;
}

/**
 * Response shape for POST /v1/general/settings/create-update
 */
export interface CreateUpdateGeneralSettingsResponse {
  response: GeneralSettingsConfigItem;
}

/**
 * General service for common data operations
 */
@Injectable({
  providedIn: 'root'
})
export class GeneralService {
  private readonly endpoint = '/v1/general/dropdowns';
  private readonly uploadEndpoint = '/v1/general/upload/file';
  private readonly deleteEndpoint = '/v1/general/delete/file';
  private readonly settingsEndpoint = '/v2/general/settings';
  private readonly createUpdateSettingsEndpoint = '/v1/general/settings/create-update';

  private settingsSubject = new BehaviorSubject<GeneralSettingsResponse | null>(null);
  public settings$ = this.settingsSubject.asObservable();

  private isSettingsInitializing = false;
  private isSettingsInitialized = false;
  private settingsInitializationPromise: Promise<void> | null = null;

  /**
   * BehaviorSubject for currencies
   */
  private currenciesSubject = new BehaviorSubject<Currency[]>([]);
  public currencies$ = this.currenciesSubject.asObservable();

  /**
   * BehaviorSubject for languages
   */
  private languagesSubject = new BehaviorSubject<GeneralLanguage[]>([]);
  public languages$ = this.languagesSubject.asObservable();

  /**
   * BehaviorSubject for countries
   */
  private countriesSubject = new BehaviorSubject<GeneralCountry[]>([]);
  public countries$ = this.countriesSubject.asObservable();

  /**
   * Flag to prevent double initialization
   */
  private isInitializing = false;
  private isInitialized = false;
  private initializationPromise: Promise<void> | null = null;

  constructor(private restfulApiService: RestfulApiService) {}

  /**
   * Get current currencies value
   */
  get currencies(): Currency[] {
    return this.currenciesSubject.value;
  }

  /**
   * Get current languages value
   */
  get languages(): GeneralLanguage[] {
    return this.languagesSubject.value;
  }

  /**
   * Get current countries value
   */
  get countries(): GeneralCountry[] {
    return this.countriesSubject.value;
  }

  /**
   * Get general dropdowns data including currencies and languages from API
   * @returns Observable of GeneralDropdownsResponse
   */
  getGeneralDropdowns(): Observable<GeneralDropdownsResponse> {
    return (this.restfulApiService.get(this.endpoint) as Observable<GeneralDropdownsResponse>).pipe(
      tap((response: GeneralDropdownsResponse) => {
        this.currenciesSubject.next(response.response.currencies);
        this.languagesSubject.next(response.response.languages);
        this.countriesSubject.next(response.response.countries ?? []);
      })
    );
  }

  /**
   * Initialize general dropdowns data
   * Call this during app initialization
   */
  initializeDropdowns(): Promise<void> {
    // If already initialized, return resolved promise
    if (this.isInitialized) {
      return Promise.resolve();
    }

    // If initialization is in progress, return the existing promise
    if (this.isInitializing && this.initializationPromise) {
      return this.initializationPromise;
    }

    // Mark as initializing
    this.isInitializing = true;

    this.initializationPromise = new Promise((resolve, reject) => {
      this.getGeneralDropdowns().subscribe({
        next: () => {
          this.isInitialized = true;
          this.isInitializing = false;
          resolve();
        },
        error: (error) => {
          console.error('Failed to initialize general dropdowns:', error);
          this.isInitializing = false;
          reject(error);
        }
      });
    });

    return this.initializationPromise;
  }

  /**
   * Get current general settings value
   */
  get settings(): GeneralSettingsResponse | null {
    return this.settingsSubject.value;
  }

  /**
   * Get general settings from API
   * @returns Observable of GeneralSettingsResponse
   */
  getGeneralSettings(): Observable<GeneralSettingsResponse> {
    return (this.restfulApiService.get(this.settingsEndpoint) as Observable<GeneralSettingsResponse>).pipe(
      tap((response: GeneralSettingsResponse) => {
        this.settingsSubject.next(response);
      })
    );
  }

  /**
   * Initialize general settings data
   * Call this during app initialization
   */
  initializeSettings(): Promise<void> {
    if (this.isSettingsInitialized) {
      return Promise.resolve();
    }

    if (this.isSettingsInitializing && this.settingsInitializationPromise) {
      return this.settingsInitializationPromise;
    }

    this.isSettingsInitializing = true;

    this.settingsInitializationPromise = new Promise((resolve, reject) => {
      this.getGeneralSettings().subscribe({
        next: () => {
          this.isSettingsInitialized = true;
          this.isSettingsInitializing = false;
          resolve();
        },
        error: (error) => {
          console.error('Failed to initialize general settings:', error);
          this.isSettingsInitializing = false;
          reject(error);
        }
      });
    });

    return this.settingsInitializationPromise;
  }

  /**
   * Create or update a single general settings entry
   * @param payload - Key and value to persist; value is serialised to a JSON string if it is an object
   * @returns Observable of CreateUpdateGeneralSettingsResponse
   */
  createUpdateGeneralSettings(payload: CreateUpdateGeneralSettingsPayload): Observable<CreateUpdateGeneralSettingsResponse> {
    const body = {
      key: payload.key,
      value: typeof payload.value === 'object' ? JSON.stringify(payload.value) : payload.value
    };

    return this.restfulApiService.post(this.createUpdateSettingsEndpoint, body) as Observable<CreateUpdateGeneralSettingsResponse>;
  }

  /**
   * Upload a file to the server
   * @param params - File upload parameters including entityUuid, fileType, secondUuid, and file
   * @returns Observable of FileUploadResponse
   * @note Bearer token is automatically added by the HTTP interceptor
   */
  uploadFile(params: FileUploadParams): Observable<FileUploadResponse> {
    const formData = new FormData();
    formData.append('entityUuid', params.entityUuid);
    formData.append('fileType', params.fileType);
    if (params.secondUuid) {
      formData.append('secondUuid', params.secondUuid);
    }
    formData.append('file', params.file);

    return this.restfulApiService.post(this.uploadEndpoint, formData) as Observable<FileUploadResponse>;
  }

  /**
   * Delete a file from the server
   * @param params - File delete parameters including entityUuid, fileType, and fileUuid
   * @returns Observable of the delete response
   * @note Bearer token is automatically added by the HTTP interceptor
   */
  deleteFile(params: FileDeleteParams): Observable<any> {
    const payload = {
      entityUuid: params.entityUuid,
      fileType: params.fileType,
      fileUuid: params.fileUuid
    };

    return this.restfulApiService.delete(this.deleteEndpoint, payload);
  }
}
